import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ShoppingCart,
    Search,
    User2,
    LogOut,
    Menu,
    X,
    Sparkles,
    Bell,
    Wallet as WalletIcon,
    Repeat,
} from "lucide-react";
import { api } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import ThemeToggle from "../ThemeToggle";
import Brand from "../Brand";
import SendbirdNotificationBell from "../SendbirdNotificationBell";
import VoiceButton from "../VoiceButton";
import LanguageSelector from "../LanguageSelector";
import FamilySwitcher from "../family/FamilySwitcher";

const inr = (n) => `₹${Number(n || 0).toFixed(0)}`;

const Navbar = () => {
    const { user, logout } = useAuth();
    const { cart } = useCart();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [walletBalance, setWalletBalance] = useState(null);
    const [unread, setUnread] = useState(0);
    const [accountOpen, setAccountOpen] = useState(false);
    const accountRef = useRef(null);

    // Poll wallet + notifications when authenticated
    useEffect(() => {
        if (!user) {
            setWalletBalance(null);
            setUnread(0);
            return;
        }
        let cancelled = false;
        const loadMeta = async () => {
            try {
                const [{ data: w }, { data: n }] = await Promise.all([
                    api.get("/wallet"),
                    api.get("/notifications", { params: { page: 1, page_size: 1 } }),
                ]);
                if (cancelled) return;
                setWalletBalance(w.balance);
                setUnread(n.unread || 0);
            } catch {
                /* silent */
            }
        };
        loadMeta();
        const t = setInterval(loadMeta, 60_000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user, location.pathname]);

    // Close account dropdown when clicking outside
    useEffect(() => {
        const onClick = (e) => {
            if (accountRef.current && !accountRef.current.contains(e.target)) {
                setAccountOpen(false);
            }
        };
        if (accountOpen) document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [accountOpen]);

    const onSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        navigate(`/products${params.toString() ? `?${params.toString()}` : ""}`);
        setOpen(false);
    };

    return (
        <header
            data-testid="site-navbar"
            className="sticky top-0 z-50 glass border-b border-zinc-200/70"
        >
            <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-4 md:px-8 lg:px-12">
                <Brand size="md" testId="nav-logo-link" />

                <form
                    onSubmit={onSearch}
                    className="relative hidden flex-1 max-w-xl items-center md:flex"
                    data-testid="nav-search-form"
                >
                    <Search className="pointer-events-none absolute left-4 h-4 w-4 text-zinc-400" />
                    <input
                        type="text"
                        placeholder={t("Search for medicines, devices, vitamins…")}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="input-clean pl-11"
                        data-testid="nav-search-input"
                    />
                </form>

                <nav className="ml-auto hidden items-center gap-1 md:flex">
                    <NavLink
                        to="/products"
                        data-testid="nav-products-link"
                        className={({ isActive }) =>
                            `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                isActive ? "text-brand-700 bg-brand-50" : "text-zinc-700 hover:text-brand-700 hover:bg-brand-50"
                            }`
                        }
                    >
                        {t("Shop")}
                    </NavLink>
                    <NavLink
                        to="/consultations"
                        data-testid="nav-consultations-link"
                        className={({ isActive }) =>
                            `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                isActive ? "text-brand-700 bg-brand-50" : "text-zinc-700 hover:text-brand-700 hover:bg-brand-50"
                            }`
                        }
                    >
                        {t("Doctors")}
                    </NavLink>
                    <NavLink
                        to="/lab-tests"
                        data-testid="nav-lab-tests-link"
                        className={({ isActive }) =>
                            `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                isActive ? "text-brand-700 bg-brand-50" : "text-zinc-700 hover:text-brand-700 hover:bg-brand-50"
                            }`
                        }
                    >
                        {t("Lab tests")}
                    </NavLink>
                    <NavLink
                        to="/insurance"
                        data-testid="nav-insurance-link"
                        className={({ isActive }) =>
                            `hidden xl:inline-block rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                isActive ? "text-brand-700 bg-brand-50" : "text-zinc-700 hover:text-brand-700 hover:bg-brand-50"
                            }`
                        }
                    >
                        {t("Insurance")}
                    </NavLink>

                    {user && (
                        <NavLink
                            to="/chat"
                            data-testid="nav-chat-link"
                            className={({ isActive }) =>
                                `inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                    isActive ? "text-brand-700 bg-brand-50" : "text-zinc-700 hover:text-brand-700 hover:bg-brand-50"
                                }`
                            }
                        >
                            <Sparkles className="h-3.5 w-3.5" /> {t("Assistant")}
                        </NavLink>
                    )}

                    {user && (
                        <NavLink
                            to="/subscriptions"
                            data-testid="nav-subscriptions-link"
                            className={({ isActive }) =>
                                `inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                    isActive ? "text-brand-700 bg-brand-50" : "text-zinc-700 hover:text-brand-700 hover:bg-brand-50"
                                }`
                            }
                        >
                            <Repeat className="h-3.5 w-3.5" /> {t("Subscriptions")}
                        </NavLink>
                    )}

                    <Link
                        to="/cart"
                        data-testid="nav-cart-link"
                        className="relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-brand-50 hover:text-brand-700"
                    >
                        <ShoppingCart className="h-4 w-4" />
                        <span>{t("Cart")}</span>
                        {cart?.total_items > 0 && (
                            <span
                                data-testid="nav-cart-badge"
                                className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-700 px-1.5 text-[10px] font-semibold text-white"
                            >
                                {cart.total_items}
                            </span>
                        )}
                    </Link>

                    {user && (
                        <Link
                            to="/wallet"
                            data-testid="nav-wallet-link"
                            title="PILLS Wallet"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-100 bg-brand-50/60 px-3 py-1.5 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100"
                        >
                            <WalletIcon className="h-3.5 w-3.5" />
                            <span data-testid="nav-wallet-balance">
                                {walletBalance == null ? "—" : inr(walletBalance)}
                            </span>
                        </Link>
                    )}

                    {user && <VoiceButton />}

                    {/* Phase 2a — Sendbird chat/notification bell */}
                    {user && <SendbirdNotificationBell />}

                    {user && (
                        <Link
                            to="/notifications"
                            data-testid="nav-notifications-link"
                            title="Notifications"
                            className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-zinc-700 hover:bg-brand-50 hover:text-brand-700 dark:text-zinc-300 dark:hover:bg-white/10"
                        >
                            <Bell className="h-4 w-4" />
                            {unread > 0 && (
                                <span
                                    data-testid="nav-notif-badge"
                                    className="absolute -right-0.5 -top-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold text-white ring-2 ring-white dark:ring-zinc-950"
                                >
                                    {unread > 99 ? "99+" : unread}
                                </span>
                            )}
                        </Link>
                    )}

                    <ThemeToggle variant="navbar" />
                    <LanguageSelector compact />
                    {user && <FamilySwitcher />}

                    {user ? (
                        <div className="relative flex items-center gap-2 pl-2" ref={accountRef}>
                            {user.elite?.active && (
                                <Link
                                    to="/elite"
                                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 hover:bg-amber-200"
                                    data-testid="nav-elite-pill"
                                    title="Elite member"
                                >
                                    <Sparkles className="h-3 w-3" /> Elite
                                </Link>
                            )}
                            <button
                                onClick={() => setAccountOpen((o) => !o)}
                                className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-800 hover:border-brand-300 hover:text-brand-700"
                                data-testid="nav-account-link"
                            >
                                <User2 className="h-4 w-4" />
                                <span className="max-w-[120px] truncate">{user.full_name || user.email}</span>
                            </button>
                            {accountOpen && (
                                <div
                                    className="absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-xl"
                                    data-testid="nav-account-menu"
                                >
                                    <Link to="/account" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Profile &amp; addresses</Link>
                                    <Link to="/reminders" onClick={() => setAccountOpen(false)} data-testid="nav-reminders-link" className="flex items-center gap-1.5 px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">
                                        <Bell className="h-3.5 w-3.5" /> Reminders
                                    </Link>
                                    <Link to="/health" onClick={() => setAccountOpen(false)} data-testid="nav-health-link" className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Health tracker</Link>
                                    <Link to="/family" onClick={() => setAccountOpen(false)} data-testid="nav-family-link" className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Family members</Link>
                                    <Link to="/caregivers" onClick={() => setAccountOpen(false)} data-testid="nav-caregivers-link" className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Caregivers</Link>
                                    <Link to="/chronic-programs" onClick={() => setAccountOpen(false)} data-testid="nav-chronic-link" className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Chronic programs</Link>
                                    <Link to="/orders" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Orders</Link>
                                    <Link to="/consultations/my" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Consultations</Link>
                                    <Link to="/lab-orders" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Lab orders</Link>
                                    <Link to="/returns" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Returns</Link>
                                    <Link to="/prescriptions" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Prescriptions</Link>
                                    <Link to="/subscriptions" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Subscriptions</Link>
                                    <Link to="/wallet" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Wallet</Link>
                                    <Link to="/support" onClick={() => setAccountOpen(false)} data-testid="nav-support-link" className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Support</Link>
                                    <Link to="/refer-earn" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Refer &amp; earn</Link>
                                    <Link to="/timeline" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Health timeline</Link>
                                    <Link to="/business" onClick={() => setAccountOpen(false)} data-testid="nav-business-link" className="flex items-center gap-1.5 border-t border-zinc-100 px-4 py-2.5 text-sm font-medium text-brand-700 hover:bg-brand-50">
                                        <span className="rounded-md bg-brand-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">B2B</span>
                                        For business
                                    </Link>
                                    <Link to="/elite" onClick={() => setAccountOpen(false)} className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50">
                                        <Sparkles className="h-3.5 w-3.5" /> PILLS Elite
                                    </Link>
                                    <Link to="/notifications" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-brand-50 hover:text-brand-700">Notifications</Link>
                                    <button
                                        onClick={() => { setAccountOpen(false); logout(); }}
                                        className="flex w-full items-center gap-2 border-t border-zinc-100 px-4 py-2.5 text-left text-sm text-zinc-600 hover:bg-rose-50 hover:text-rose-600"
                                        data-testid="nav-logout-btn"
                                    >
                                        <LogOut className="h-4 w-4" /> Sign out
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 pl-2">
                            <Link
                                to="/login"
                                className="rounded-full px-4 py-2 text-sm font-medium text-zinc-800 hover:text-brand-700"
                                data-testid="nav-login-link"
                            >
                                {t("Sign in")}
                            </Link>
                            <Link to="/signup" className="btn-brand !py-2 !px-5 !text-sm" data-testid="nav-signup-link">
                                {t("Get started")}
                            </Link>
                        </div>
                    )}
                </nav>

                <button
                    className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-full text-zinc-700 hover:bg-zinc-100 md:hidden"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className="border-t border-zinc-200/70 bg-white px-4 py-4 md:hidden">
                    <form onSubmit={onSearch} className="relative mb-3 flex items-center">
                        <Search className="pointer-events-none absolute left-4 h-4 w-4 text-zinc-400" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="input-clean pl-11"
                            data-testid="nav-mobile-search-input"
                        />
                    </form>
                    <div className="flex flex-col gap-1">
                        <Link to="/products" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Shop</Link>
                        <Link to="/consultations" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Doctors</Link>
                        <Link to="/lab-tests" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Lab tests</Link>
                        <Link to="/insurance" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Insurance</Link>
                        {user && (
                            <Link to="/chat" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Assistant</Link>
                        )}
                        {user && (
                            <Link to="/consultations/my" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">My consultations</Link>
                        )}
                        {user && (
                            <Link to="/lab-orders" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">My lab orders</Link>
                        )}
                        {user && (
                            <Link to="/reminders" onClick={() => setOpen(false)} data-testid="nav-mobile-reminders-link" className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Reminders</Link>
                        )}
                        {user && (
                            <Link to="/health" onClick={() => setOpen(false)} data-testid="nav-mobile-health-link" className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Health tracker</Link>
                        )}
                        {user && (
                            <Link to="/family" onClick={() => setOpen(false)} data-testid="nav-mobile-family-link" className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Family</Link>
                        )}
                        {user && (
                            <Link to="/chronic-programs" onClick={() => setOpen(false)} data-testid="nav-mobile-chronic-link" className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Chronic programs</Link>
                        )}
                        {user && (
                            <Link to="/prescriptions" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Prescriptions</Link>
                        )}
                        {user && (
                            <Link to="/subscriptions" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Subscriptions</Link>
                        )}
                        {user && (
                            <Link to="/orders" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Orders</Link>
                        )}
                        {user && (
                            <Link to="/wallet" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">
                                Wallet {walletBalance != null ? `(${inr(walletBalance)})` : ""}
                            </Link>
                        )}
                        {user && (
                            <Link to="/notifications" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">
                                Notifications {unread > 0 ? `(${unread})` : ""}
                            </Link>
                        )}
                        <Link to="/cart" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">
                            Cart {cart?.total_items > 0 ? `(${cart.total_items})` : ""}
                        </Link>
                        {user ? (
                            <>
                                <Link to="/account" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Account</Link>
                                <button onClick={() => { setOpen(false); logout(); }} className="rounded-lg px-4 py-2 text-left text-sm font-medium hover:bg-brand-50">Sign out</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-brand-50">Sign in</Link>
                                <Link to="/signup" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50">Get started</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
